import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Send, User, Mail, Building } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { publicationAPI, coauthorAPI } from '../services/api';
import { Publication, CoauthorRequest } from '../types';
import toast from 'react-hot-toast';

const CoauthorRequestPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [publication, setPublication] = useState<Publication | null>(null);
  const [requests, setRequests] = useState<CoauthorRequest[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadPublicationData();
    }
  }, [id]);

  const loadPublicationData = async () => {
    try {
      setIsLoading(true);
      const [publicationData, requestsData] = await Promise.all([
        publicationAPI.getPublication(parseInt(id!)),
        coauthorAPI.getCoauthorRequests(parseInt(id!))
      ]);
      
      setPublication(publicationData);
      setRequests(requestsData);
    } catch (error: any) {
      toast.error('Failed to load publication data');
      console.error('Error loading publication data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!newMessage.trim()) {
      setError('Message is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      await coauthorAPI.requestCoauthor(parseInt(id!), { message: newMessage });
      toast.success('Co-author request submitted successfully!');
      setNewMessage('');
      loadPublicationData(); // Reload to show the new request
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors as Array<{ msg: string }>|undefined;
      if (apiErrors && apiErrors.length) {
        setError(apiErrors.map(e => e.msg).join(' '));
      } else {
        setError(error?.response?.data?.error || 'Failed to submit co-author request');
      }
      toast.error('Failed to submit co-author request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRespondToRequest = async (requestId: number, status: 'approved' | 'rejected') => {
    try {
      await coauthorAPI.respondToRequest(requestId, {
        status,
        responseMessage: undefined // Message is optional
      });
      toast.success(`Co-author request ${status} successfully`);
      loadPublicationData();
    } catch (error: any) {
      toast.error(`Failed to ${status} co-author request`);
    }
  };

  const getStageText = (stage: string) => {
    switch (stage) {
      case '1': return 'Ideation';
      case '2': return 'Review & Team';
      case '3': return 'Data Analysis';
      case '4': return 'Manuscript';
      case 'published': return 'Published';
      default: return 'Unknown';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case '1': return 'bg-blue-100 text-blue-800';
      case '2': return 'bg-yellow-100 text-yellow-800';
      case '3': return 'bg-purple-100 text-purple-800';
      case '4': return 'bg-indigo-100 text-indigo-800';
      case 'published': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading publication details...</p>
        </div>
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Publication Not Found</h2>
          <button
            onClick={() => navigate('/publications')}
            className="text-green-600 hover:text-green-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isLeadAuthor = user?.id === publication.leadAuthorId;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canJoin = !isLeadAuthor && (publication.stage === '2' || publication.stage === '3');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <button
            onClick={() => navigate('/publications')}
            className="flex items-center space-x-1 text-green-600 hover:text-green-700"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Dashboard</span>
          </button>
          <span>/</span>
          <span>{publication.title}</span>
          <span>/</span>
          <span>Co-author Requests</span>
        </div>

        {/* Publication Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{publication.title}</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(publication.stage)}`}>
                {getStageText(publication.stage)}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <Building className="h-4 w-4" />
              <span>{publication.dataSource}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <User className="h-4 w-4" />
              <span>Lead: {publication.leadAuthorName}</span>
            </div>
          </div>

          <p className="text-gray-700">{publication.abstract}</p>
        </div>

        {/* Co-author Requests */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <MessageCircle className="h-5 w-5 mr-2 text-green-600" />
            Co-author Requests ({requests.length})
          </h2>

          {requests.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No requests yet</h3>
              <p className="text-gray-600">Co-author requests will appear here when submitted.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                        {((request.requesterName || `${request.first_name || ''} ${request.last_name || ''}`).trim()).split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{request.requesterName || `${request.first_name || ''} ${request.last_name || ''}`.trim()}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {request.requesterEmail || request.email}
                          </span>
                          {(request.requesterInstitution || request.institution) && (
                            <span className="flex items-center">
                              <Building className="h-3 w-3 mr-1" />
                              {request.requesterInstitution || request.institution}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(request.createdAt)}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{request.message}</p>

                  {request.responseMessage && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm text-gray-600">
                        <strong>Response:</strong> {request.responseMessage}
                      </p>
                    </div>
                  )}

                  {request.status === 'pending' && (isLeadAuthor || isAdmin) && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleRespondToRequest(request.id, 'approved')}
                        className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleRespondToRequest(request.id, 'rejected')}
                        className="flex items-center space-x-1 bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        <span>Reject</span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New Request Form */}
        {canJoin && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Send className="h-5 w-5 mr-2 text-green-600" />
              Submit Your Request
            </h2>

            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message to Lead Author *
                </label>
                <textarea
                  id="message"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  required
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Introduce yourself, explain your interest in this research, share your relevant experience, and include your contact information..."
                />
                {error && (
                  <div className="mt-2 text-sm text-red-600">{error}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/publications')}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !newMessage.trim()}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="h-4 w-4" />
                  <span>{isSubmitting ? 'Submitting...' : 'Submit Request'}</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {!canJoin && !isLeadAuthor && !isAdmin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">
              Co-author requests are only available for publications in Stage 2 (Review & Team) or Stage 3 (Data Analysis).
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoauthorRequestPage;
